import React, { useCallback, useState } from 'react';
import { useArealplan, erIkkeGjeldende } from '@origo/planregister';
import PlanDetailShell, { type PlanTab } from './components/PlanDetailShell';
import PlanActionsContainer from './components/PlanActionsContainer';
import ReplacementNoticeContainer from './components/ReplacementNoticeContainer';
import PlanLandingContainer from './routes/landing/PlanLandingContainer';
import NewPlanContainer from './routes/new-plan/NewPlanContainer';
import PlanInfoContainer from './routes/plan-info/PlanInfoContainer';
import UnsavedChangesDialog from './components/UnsavedChangesDialog';
import type { UnsavedChangesGuard } from './hooks/useUnsavedChangesGuard';
import { rememberRecentPlan } from './utils/recentPlans';
import ProcessingsContainer from './routes/processings/ProcessingsContainer';
import RelationsContainer from './routes/relations/RelationsContainer';
import DocumentsContainer from './routes/documents/DocumentsContainer';
import DispensationsContainer from './routes/dispensations/DispensationsContainer';

type Route =
    | { kind: 'landing' }
    | { kind: 'new' }
    | { kind: 'plan'; napId: string; tab: Exclude<PlanTab, 'kart'> };

interface PlanregisterViewProps {
    /**
     * Vakten eies av app-skallet, så også handlinger utenfor visningen (som
     * «Logg ut» i headeren) varsler om ulagrede endringer.
     */
    unsaved: UnsavedChangesGuard;
}

const PlanregisterView: React.FC<PlanregisterViewProps> = ({ unsaved }) => {
    const [route, setRoute] = useState<Route>({ kind: 'landing' });

    // Plan-tittel hentes på view-nivå for shell-headeren. Samme query brukes
    // av PlanInfoContainer — React-Query deduplikerer på query-key, så dette
    // koster ingenting ekstra.
    const napId = route.kind === 'plan' ? route.napId : undefined;
    const { data: plan } = useArealplan(napId);

    // Planinfo-skjemaet melder ulagrede endringer hit; fanebytte og «tilbake»
    // går via vakten så brukeren kan velge å forkaste eller bli.
    const { guard, setDirty } = unsaved;

    const handleSelectPlan = useCallback((selected: string) => {
        rememberRecentPlan(selected);
        setRoute({ kind: 'plan', napId: selected, tab: 'info' });
    }, []);

    const handleCreatePlan = useCallback(() => {
        setRoute({ kind: 'new' });
    }, []);

    const handlePlanCreated = useCallback((napId: string) => {
        rememberRecentPlan(napId);
        setRoute({ kind: 'plan', napId, tab: 'info' });
    }, []);

    const handleCancelCreate = useCallback(() => {
        setRoute({ kind: 'landing' });
    }, []);

    const handleTabChange = useCallback((tab: PlanTab) => {
        // Klikk på aktiv fane er ingen navigasjon og skal ikke varsle.
        if (route.kind !== 'plan' || tab === 'kart' || tab === route.tab) return;
        guard(() => setRoute({ ...route, tab }));
    }, [route, guard]);

    // Lenke til en annen plan (f.eks. erstattende plan) — samme vakt som fanebytte.
    const handleOpenPlan = useCallback((target: string) => {
        guard(() => {
            rememberRecentPlan(target);
            setRoute({ kind: 'plan', napId: target, tab: 'info' });
        });
    }, [guard]);

    const handleBackToMap = useCallback(() => {
        guard(() => setRoute({ kind: 'landing' }));
    }, [guard]);

    // Planen er slettet — ingenting å lagre, så ingen vakt.
    const handlePlanDeleted = useCallback(() => {
        setDirty(false);
        setRoute({ kind: 'landing' });
    }, [setDirty]);

    if (route.kind === 'landing') {
        return (
            <PlanLandingContainer
                onSelectPlan={handleSelectPlan}
                onCreatePlan={handleCreatePlan}
            />
        );
    }

    if (route.kind === 'new') {
        return (
            <NewPlanContainer
                onCreated={handlePlanCreated}
                onCancel={handleCancelCreate}
            />
        );
    }

    const ikkeGjeldende = plan ? erIkkeGjeldende(plan.planstatus) : false;

    return (
        <PlanDetailShell
            // Ny plan (f.eks. via lenke til erstattende plan) = nytt skall, så
            // skjemaene i fanene ikke beholder verdier fra forrige plan.
            key={route.napId}
            activeTab={route.tab}
            onTabChange={handleTabChange}
            onBackToMap={handleBackToMap}
            planTitle={plan?.plannavn}
            identifiers={plan ? {
                nasjonalArealplanId: plan.nasjonalArealplanId,
                planId: plan.planId,
                admomrkode: plan.admomrkode,
            } : undefined}
            ikkeGjeldende={ikkeGjeldende}
            actions={<PlanActionsContainer napId={route.napId} onDeleted={handlePlanDeleted} />}
            notice={plan && (
                <ReplacementNoticeContainer
                    napId={route.napId}
                    ikkeGjeldende={ikkeGjeldende}
                    onOpenPlan={handleOpenPlan}
                />
            )}
        >
            {route.tab === 'info' && (
                <PlanInfoContainer napId={route.napId} onDirtyChange={setDirty} />
            )}
            {route.tab === 'behandlinger' && (
                <ProcessingsContainer napId={route.napId} />
            )}
            {route.tab === 'planforhold' && (
                <RelationsContainer napId={route.napId} />
            )}
            {route.tab === 'dokumenter' && (
                <DocumentsContainer napId={route.napId} />
            )}
            {route.tab === 'dispensasjoner' && (
                <DispensationsContainer napId={route.napId} />
            )}
            <UnsavedChangesDialog
                open={unsaved.isConfirming}
                onStay={unsaved.cancel}
                onDiscard={unsaved.confirm}
            />
        </PlanDetailShell>
    );
};

export default PlanregisterView;
